import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import ReactSelect from 'react-select';
import { getBanks } from '../api/bankApi';
import { createBorrowerTransaction, getBorrowerTransactionSummary, getBorrowerTransactions } from '../api/borrowerTransactionApi';
import { createLenderTransaction, getLenderTransactionSummary, getLenderTransactions } from '../api/lenderTransactionApi';

const PageSection = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const Intro = styled.div`
  max-width: 520px;
  color: #475467;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const SearchInput = styled.input`
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  padding: 9px 16px 9px 36px;
  font-size: 14px;
  min-width: 220px;
  background: #fff
    url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="%2394a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Ccircle cx="11" cy="11" r="8"/%3E%3Cpath d="m21 21-4.35-4.35"/%3E%3C/svg%3E')
    no-repeat 12px center;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
  }
`;

const AddButton = styled.button`
  padding: 10px 16px;
  border-radius: 999px;
  border: none;
  background: #111322;
  color: #f9fafb;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.2);

  &:hover {
    background: #020617;
  }
`;

const SummaryStrip = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: #fafbfd;
`;

const SummaryLabel = styled.span`
  color: #475467;
  font-size: 14px;
`;

const SummaryValue = styled.span`
  font-weight: 700;
  font-size: 24px;
  color: #0f172a;
`;

const Table = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  overflow: hidden;
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1.1fr 1.1fr 2fr 0.9fr;
  background: #f8fafc;
  padding: 14px 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1.1fr 1.1fr 2fr 0.9fr;
  padding: 18px 20px;
  align-items: center;
  border-top: 1px solid #eef2ff;
  background: #ffffff;

  &:nth-child(even) {
    background: #fdfdfd;
  }
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RecordName = styled.span`
  font-weight: 600;
  color: #0f172a;
`;

const RecordMeta = styled.span`
  font-size: 13px;
  color: #94a3b8;
`;

const AmountCell = styled.span`
  font-weight: 600;
  color: #0f172a;
`;

const DescriptionCell = styled.p`
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 1.4;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  font-size: 12px;
  color: #0f172a;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

const DangerButton = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: none;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #fecaca;
  }
`;

const EmptyPlaceholder = styled.div`
  padding: 32px;
  border: 1px dashed #cbd5f5;
  border-radius: 18px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;

const StatusBanner = styled.div`
  padding: 12px 16px;
  border-radius: 14px;
  background: ${(props) => (props.$error ? '#fee2e2' : '#dbeafe')};
  color: ${(props) => (props.$error ? '#b91c1c' : '#1d4ed8')};
  font-size: 14px;
`;

const LoadingBlock = styled.div`
  padding: 32px;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  text-align: center;
  color: #475467;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 40;
`;

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 24px 24px 20px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.25);
`;

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
  color: #0f172a;
`;

const ModalSubtitle = styled.p`
  margin: 0 0 18px;
  font-size: 13px;
  color: #64748b;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #0f172a;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 8px 10px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
  }
`;

const TextArea = styled.textarea`
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 8px 10px;
  font-size: 14px;
  resize: vertical;
  min-height: 96px;
  outline: none;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
  }
`;

const RadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const RadioLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #0f172a;
`;

const RadioInput = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #111322;
`;

const ModalActions = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const SecondaryButton = styled.button`
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  padding: 8px 14px;
  border-radius: 999px;
  border: none;
  background: #111322;
  color: #f9fafb;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};

  &:hover {
    background: #020617;
  }
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #dc2626;
`;

const asLedgerRecord = (record = {}, fallbackLabel = 'Entry') => {
  const numericAmount = Number(record?.amount ?? record?.beginningAmount ?? 0);
  return {
    id:
      record?.id ||
      `${fallbackLabel.toLowerCase().replace(/\s+/g, '-')}-${Math.random().toString(36).slice(2, 8)}`,
    name: record?.name || `${fallbackLabel} entry`,
    amount: Number.isFinite(numericAmount) ? numericAmount : 0,
    description: record?.description || ''
  };
};

const PartyLedgerPage = ({
  partyLabel,
  introText,
  initialRecords = [],
  apiClient = null,
  currencyCode = 'LKR',
  currencyLocale = 'en-LK'
}) => {
  const isLenderLedger = partyLabel === 'Lender';
  const isBorrowerLedger = partyLabel === 'Borrower';
  const supportsTransactions = isLenderLedger || isBorrowerLedger;
  const hasRemoteSource = Boolean(apiClient?.fetchAll);
  const [records, setRecords] = useState(() => (initialRecords || []).map((item) => asLedgerRecord(item, partyLabel)));
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(hasRemoteSource);
  const [loadError, setLoadError] = useState(null);

  const [banks, setBanks] = useState([]);
  const [banksError, setBanksError] = useState(null);

  const [isTxnModalOpen, setIsTxnModalOpen] = useState(false);
  const [txnPartyId, setTxnPartyId] = useState('');
  const [txnAmount, setTxnAmount] = useState('');
  const [txnDate, setTxnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [txnDescription, setTxnDescription] = useState('');
  const [txnSourceType, setTxnSourceType] = useState('cash');
  const [txnDirection, setTxnDirection] = useState('pay');
  const [txnBankAccountId, setTxnBankAccountId] = useState('');
  const [txnSaving, setTxnSaving] = useState(false);
  const [txnError, setTxnError] = useState(null);
  const [partyPaidTotals, setPartyPaidTotals] = useState({});
  const [partyTransactions, setPartyTransactions] = useState([]);
  const [partyTransactionsLoading, setPartyTransactionsLoading] = useState(false);
  const [partyTransactionsError, setPartyTransactionsError] = useState(null);
  const [detailParty, setDetailParty] = useState(null);

  const labelLower = (partyLabel || 'party').toLowerCase();

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(currencyLocale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
    [currencyLocale, currencyCode]
  );

  const normalizeList = useCallback((list = []) => list.map((item) => asLedgerRecord(item, partyLabel)), [partyLabel]);

  const applyInitialRecords = useCallback(() => {
    setRecords(normalizeList(initialRecords || []));
  }, [initialRecords, normalizeList]);

  const fetchRecords = useCallback(async () => {
    if (!hasRemoteSource || !apiClient?.fetchAll) {
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      const response = await apiClient.fetchAll();
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      setRecords(normalizeList(data));
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to load data';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, [apiClient, hasRemoteSource, normalizeList]);

  useEffect(() => {
    if (hasRemoteSource) {
      fetchRecords();
    } else {
      applyInitialRecords();
      setLoading(false);
    }
  }, [hasRemoteSource]);

  useEffect(() => {
    if (!supportsTransactions) {
      return;
    }

    const loadBanks = async () => {
      try {
        setBanksError(null);
        const response = await getBanks();
        const data = Array.isArray(response?.data?.data) ? response.data.data : [];
        setBanks(data);
      } catch (error) {
        const message = error?.response?.data?.message || 'Failed to load bank accounts';
        setBanksError(message);
      }
    };

    loadBanks();
  }, [supportsTransactions]);

  const loadPartyPaidTotals = useCallback(async () => {
    if (!supportsTransactions) {
      return;
    }

    try {
      const response = await (isLenderLedger ? getLenderTransactionSummary() : getBorrowerTransactionSummary());
      const rows = response.data?.data ?? [];
      const next = {};
      rows.forEach((row) => {
        const id = isLenderLedger ? row.lenderId : row.borrowerId;
        if (id != null) {
          const value = isLenderLedger
            ? row.netPaid !== undefined
              ? row.netPaid
              : row.totalPaid
            : row.netGiven !== undefined
            ? row.netGiven
            : row.totalGiven;
          next[id] = Number(value) || 0;
        }
      });
      setPartyPaidTotals(next);
    } catch (error) {
      console.error('Error fetching transaction summary:', error);
    }
  }, [isLenderLedger, supportsTransactions]);

  useEffect(() => {
    loadPartyPaidTotals();
  }, [loadPartyPaidTotals]);

  const bankSelectOptions = useMemo(
    () =>
      banks.map((bank) => {
        const holder = bank.accountHolderName || bank.account_holder_name || '';
        const labelParts = [bank.name, holder].filter(Boolean);
        return {
          value: String(bank.id),
          label: labelParts.join(' — ') || bank.name || 'Bank account'
        };
      }),
    [banks]
  );

  const selectedBankOption = useMemo(
    () =>
      txnBankAccountId
        ? bankSelectOptions.find((option) => option.value === String(txnBankAccountId)) || null
        : null,
    [bankSelectOptions, txnBankAccountId]
  );

  const bankSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        borderRadius: 10,
        borderColor: state.isFocused ? '#6366f1' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 1px rgba(99, 102, 241, 0.2)' : 'none',
        minHeight: 44,
        '&:hover': {
          borderColor: '#6366f1'
        }
      }),
      menu: (base) => ({
        ...base,
        borderRadius: 12
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? '#eef2ff' : '#fff',
        color: '#0f172a'
      })
    }),
    []
  );

  const totalAmount = useMemo(
    () =>
      records.reduce((sum, record) => {
        const numericAmount = Number(record.amount || 0);
        return sum + (Number.isNaN(numericAmount) ? 0 : numericAmount);
      }, 0),
    [records]
  );

  const totalCurrentBalance = useMemo(() => {
    if (!supportsTransactions) {
      return totalAmount;
    }

    return records.reduce((sum, record) => {
      const beginning = Number(record.amount || 0) || 0;
      const net = partyPaidTotals[record.id] || 0;
      return sum + beginning + net;
    }, 0);
  }, [records, partyPaidTotals, supportsTransactions, totalAmount]);

  const filteredRecords = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return records;
    }

    return records.filter((record) => {
      const nameMatch = record.name?.toLowerCase().includes(term);
      const descriptionMatch = record.description?.toLowerCase().includes(term);
      return nameMatch || descriptionMatch;
    });
  }, [records, searchTerm]);

  const resetForm = () => {
    setName('');
    setAmount('');
    setDescription('');
    setFormError(null);
  };

  const openPartyDetail = async (record) => {
    if (!supportsTransactions) return;

    setDetailParty(record);
    setPartyTransactions([]);
    setPartyTransactionsError(null);
    setPartyTransactionsLoading(true);

    try {
      const idField = isLenderLedger ? 'lenderId' : 'borrowerId';
      const fetchFn = isLenderLedger ? getLenderTransactions : getBorrowerTransactions;
      const response = await fetchFn({ [idField]: record.id });
      const rows = response.data?.data ?? [];
      setPartyTransactions(rows);
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to load transactions';
      setPartyTransactionsError(message);
    } finally {
      setPartyTransactionsLoading(false);
    }
  };

  const closePartyDetail = () => {
    setDetailParty(null);
  };

  const openTxnModal = () => {
    if (!supportsTransactions) return;
    setTxnError(null);
    setTxnAmount('');
    setTxnDescription('');
    setTxnDate(new Date().toISOString().slice(0, 10));
    setTxnSourceType('cash');
    setTxnDirection('pay');
    setTxnBankAccountId('');

    if (records.length > 0 && !txnPartyId) {
      setTxnPartyId(String(records[0].id));
    }

    setIsTxnModalOpen(true);
  };

  const closeTxnModal = () => {
    if (txnSaving) return;
    setIsTxnModalOpen(false);
  };

  const openCreateModal = () => {
    resetForm();
    setEditingRecord(null);
    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setName(record.name || '');
    setAmount(String(record.amount ?? ''));
    setDescription(record.description || '');
    setFormError(null);
    setEditingRecord(record);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
  };

  const buildId = () => {
    const slug = labelLower.replace(/\s+/g, '-');
    return `${slug}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  };

  const handleDelete = (record) => {
    Swal.fire({
      title: `Remove this ${labelLower}?`,
      text: record.name,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111322',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete'
    }).then(async (result) => {
      if (!result.isConfirmed) {
        return;
      }

      try {
        if (hasRemoteSource && apiClient?.remove) {
          await apiClient.remove(record.id);
          await fetchRecords();
        } else {
          setRecords((prev) => prev.filter((item) => item.id !== record.id));
        }

        Swal.fire({
          title: 'Deleted',
          text: `${partyLabel} removed from the list`,
          icon: 'success',
          timer: 1400,
          showConfirmButton: false
        });
      } catch (error) {
        const message = error?.response?.data?.message || 'Failed to delete entry';
        Swal.fire({ title: 'Delete failed', text: message, icon: 'error' });
      }
    });
  };

  const handleTxnSubmit = async (event) => {
    event.preventDefault();

    if (!supportsTransactions) {
      return;
    }

    const partyIdNumeric = Number(txnPartyId);
    const amountNumeric = Number(txnAmount);

    if (!Number.isInteger(partyIdNumeric) || partyIdNumeric <= 0) {
      setTxnError(`Select a ${labelLower}`);
      return;
    }

    if (!Number.isFinite(amountNumeric) || amountNumeric <= 0) {
      setTxnError('Amount must be greater than zero');
      return;
    }

    if (!txnDate) {
      setTxnError('Date is required');
      return;
    }

    if (txnSourceType === 'bank') {
      const bankIdNumeric = Number(txnBankAccountId);
      if (!Number.isInteger(bankIdNumeric) || bankIdNumeric <= 0) {
        setTxnError('Select a valid bank when paying from bank');
        return;
      }
    }

    const partyField = isLenderLedger ? 'lenderId' : 'borrowerId';
    const payload = {
      [partyField]: partyIdNumeric,
      amount: amountNumeric,
      description: txnDescription.trim(),
      date: txnDate,
      sourceType: txnSourceType,
      direction: txnDirection,
      bankAccountId: txnSourceType === 'bank' ? Number(txnBankAccountId) : null
    };

    setTxnSaving(true);
    setTxnError(null);

    try {
      if (isLenderLedger) {
        await createLenderTransaction(payload);
      } else {
        await createBorrowerTransaction(payload);
      }

      Swal.fire({
        title: `${partyLabel} transaction recorded`,
        icon: 'success',
        timer: 1400,
        showConfirmButton: false
      });

      setIsTxnModalOpen(false);
      await loadPartyPaidTotals();
    } catch (error) {
      const message =
        error?.response?.data?.message || error.message || `Failed to record ${labelLower} transaction`;
      setTxnError(message);
    } finally {
      setTxnSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!name.trim()) {
      setFormError('Name is required');
      return;
    }

    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount)) {
      setFormError('Beginning amount must be a number');
      return;
    }

    const normalizedDescription = description.trim();
    const localPayload = {
      id: editingRecord?.id || buildId(),
      name: name.trim(),
      amount: numericAmount,
      description: normalizedDescription
    };

    setSaving(true);
    setFormError(null);

    try {
      if (hasRemoteSource) {
        const apiPayload = {
          name: localPayload.name,
          beginningAmount: localPayload.amount,
          description: normalizedDescription
        };

        if (editingRecord && apiClient?.update) {
          await apiClient.update(editingRecord.id, apiPayload);
        } else if (!editingRecord && apiClient?.create) {
          await apiClient.create(apiPayload);
        } else {
          throw new Error('Operation not supported by API client');
        }

        await fetchRecords();
      } else {
        setRecords((prev) => {
          if (editingRecord) {
            return prev.map((record) => (record.id === editingRecord.id ? localPayload : record));
          }
          return [...prev, localPayload];
        });
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to save entry';
      setFormError(message);
      setSaving(false);
      return;
    }

    setSaving(false);
    setIsModalOpen(false);
    Swal.fire({
      title: editingRecord ? 'Updated' : 'Added',
      text: `${partyLabel} entry saved`,
      icon: 'success',
      timer: 1400,
      showConfirmButton: false
    });
  };

  return (
    <PageSection>
      <Header>
        <Intro>
          <h2 style={{ margin: 0, color: '#0f172a' }}>{partyLabel} ledger</h2>
          <p style={{ margin: '8px 0 0' }}>{introText}</p>
        </Intro>
        <ControlsRow>
          <SearchInput
            placeholder={`Search ${labelLower}s`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <AddButton type="button" onClick={openCreateModal}>
            + Add {partyLabel}
          </AddButton>
          {supportsTransactions && (
            <AddButton type="button" onClick={openTxnModal}>
              {partyLabel} Transaction
            </AddButton>
          )}
        </ControlsRow>
      </Header>

      <SummaryStrip>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <SummaryLabel>
            {isLenderLedger
              ? `Total current balance (${currencyCode}) to pay lenders`
              : supportsTransactions
              ? `Total current balance (${currencyCode}) to receive from borrowers`
              : `Total beginning amount (${currencyCode})`}
          </SummaryLabel>
          {supportsTransactions && (
            <SummaryLabel style={{ fontSize: 12 }}>
              Beginning total: {currencyFormatter.format(totalAmount)}
            </SummaryLabel>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <SummaryValue>{currencyFormatter.format(totalCurrentBalance)}</SummaryValue>
          {supportsTransactions && (
            <span style={{ display: 'block', fontSize: 13, color: '#64748b' }}>
              {isLenderLedger ? 'Net amount still owed to lenders' : 'Net amount still outstanding'}
            </span>
          )}
        </div>
      </SummaryStrip>

      {loadError && <StatusBanner $error>{loadError}</StatusBanner>}

      {loading ? (
        <LoadingBlock>Loading {labelLower} data...</LoadingBlock>
      ) : records.length === 0 ? (
        <EmptyPlaceholder>No {labelLower} data yet. Add the first record to get started.</EmptyPlaceholder>
      ) : (
        <Table>
          <TableHead>
            <span>{partyLabel} name</span>
            <span>Beginning amount ({currencyCode})</span>
            {supportsTransactions && <span>Current balance ({currencyCode})</span>}
            <span>Description</span>
            <span style={{ textAlign: 'right' }}>Actions</span>
          </TableHead>
          <TableBody>
            {filteredRecords.length === 0 ? (
              <EmptyPlaceholder style={{ border: 'none', borderRadius: 0 }}>No matches for "{searchTerm}"</EmptyPlaceholder>
            ) : (
              filteredRecords.map((record) => (
                <TableRow key={record.id}>
                  <NameCell>
                    <RecordName>{record.name}</RecordName>
                    <RecordMeta>#{record.id?.toString().split('-')[0] || 'entry'}</RecordMeta>
                  </NameCell>
                  <AmountCell>{currencyFormatter.format(Number(record.amount || 0))}</AmountCell>
                  {supportsTransactions && (() => {
                    const beginning = Number(record.amount || 0) || 0;
                    const net = partyPaidTotals[record.id] || 0;
                    const balance = beginning + net;
                    return <AmountCell>{currencyFormatter.format(balance)}</AmountCell>;
                  })()}
                  <DescriptionCell>{record.description || '—'}</DescriptionCell>
                  <ActionsCell>
                    {supportsTransactions && (
                      <ActionButton type="button" onClick={() => openPartyDetail(record)}>
                        View
                      </ActionButton>
                    )}
                    <ActionButton type="button" onClick={() => openEditModal(record)}>
                      Edit
                    </ActionButton>
                    <DangerButton type="button" onClick={() => handleDelete(record)}>
                      Delete
                    </DangerButton>
                  </ActionsCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {isModalOpen && (
        <ModalBackdrop>
          <ModalCard>
            <ModalTitle>{editingRecord ? `Edit ${partyLabel}` : `Add ${partyLabel}`}</ModalTitle>
            <ModalSubtitle>Provide the beginning amount and short description.</ModalSubtitle>
            <form onSubmit={handleSubmit}>
              <FormField>
                <Label htmlFor="name">{partyLabel} name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={`${partyLabel} name`} />
              </FormField>
              <FormField>
                <Label htmlFor="amount">Beginning amount ({currencyCode})</Label>
                <Input
                  id="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  type="number"
                  step="0.01"
                />
              </FormField>
              <FormField>
                <Label htmlFor="description">Description</Label>
                <TextArea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Purpose or context"
                />
              </FormField>
              {formError && <ErrorText>{formError}</ErrorText>}
              <ModalActions>
                <SecondaryButton type="button" onClick={closeModal} disabled={saving}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={saving}>
                  {editingRecord ? 'Update' : 'Save'}
                </PrimaryButton>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalBackdrop>
      )}

      {supportsTransactions && isTxnModalOpen && (
        <ModalBackdrop>
          <ModalCard>
            <ModalTitle>Add {labelLower} transaction</ModalTitle>
            <ModalSubtitle>
              Record paying a {labelLower} or receiving funds from them via cash or bank.
            </ModalSubtitle>
            <form onSubmit={handleTxnSubmit}>
              <FormField>
                <Label htmlFor="txn-party">{partyLabel}</Label>
                <select
                  id="txn-party"
                  value={txnPartyId}
                  onChange={(e) => setTxnPartyId(e.target.value)}
                  style={{
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    padding: '8px 10px',
                    fontSize: 14
                  }}
                >
                  <option value="">Select {labelLower}</option>
                  {records.map((record) => (
                    <option key={record.id} value={record.id}>
                      {record.name}
                    </option>
                  ))}
                </select>
              </FormField>

              {(() => {
                const selected = records.find((r) => String(r.id) === String(txnPartyId));
                if (!selected) return null;
                const beginning = Number(selected.amount || 0) || 0;
                const net = partyPaidTotals[selected.id] || 0;
                const balance = beginning + net;
                const balanceLabel = isLenderLedger ? 'Current balance to pay' : 'Current balance to receive';
                return (
                  <StatusBanner style={{ marginBottom: 8 }}>
                    {balanceLabel}: <strong>{currencyFormatter.format(balance)}</strong>
                  </StatusBanner>
                );
              })()}

              <FormField>
                <Label htmlFor="txn-amount">Amount</Label>
                <Input
                  id="txn-amount"
                  type="number"
                  step="0.01"
                  value={txnAmount}
                  onChange={(e) => setTxnAmount(e.target.value)}
                  placeholder="0.00"
                />
              </FormField>

              <FormField>
                <Label htmlFor="txn-date">Date</Label>
                <Input
                  id="txn-date"
                  type="date"
                  value={txnDate}
                  onChange={(e) => setTxnDate(e.target.value)}
                />
              </FormField>

              <FormField>
                <Label>Type</Label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ fontSize: 13, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="radio"
                      name="txn-source-type"
                      value="cash"
                      checked={txnSourceType === 'cash'}
                      onChange={() => setTxnSourceType('cash')}
                    />
                    Cash
                  </label>
                  <label style={{ fontSize: 13, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="radio"
                      name="txn-source-type"
                      value="bank"
                      checked={txnSourceType === 'bank'}
                      onChange={() => setTxnSourceType('bank')}
                    />
                    Bank
                  </label>
                </div>
              </FormField>

              {txnSourceType === 'bank' && (
                <FormField>
                  <Label htmlFor="txn-bank">Bank account</Label>
                  <ReactSelect
                    inputId="txn-bank"
                    classNamePrefix="bank-select"
                    placeholder="Select bank and holder"
                    isDisabled={banks.length === 0}
                    options={bankSelectOptions}
                    value={selectedBankOption}
                    onChange={(option) => setTxnBankAccountId(option?.value ?? '')}
                    isClearable
                    styles={bankSelectStyles}
                  />
                  {banksError && <ErrorText>{banksError}</ErrorText>}
                </FormField>
              )}

              <FormField>
                <Label>Flow</Label>
                <RadioRow>
                  <RadioLabel>
                    <RadioInput
                      type="radio"
                      name="txn-direction"
                      value="pay"
                      checked={txnDirection === 'pay'}
                      onChange={() => setTxnDirection('pay')}
                    />
                    Pay {labelLower}
                  </RadioLabel>
                  <RadioLabel>
                    <RadioInput
                      type="radio"
                      name="txn-direction"
                      value="receive"
                      checked={txnDirection === 'receive'}
                      onChange={() => setTxnDirection('receive')}
                    />
                    Receive from {labelLower}
                  </RadioLabel>
                </RadioRow>
              </FormField>

              <FormField>
                <Label htmlFor="txn-description">Description</Label>
                <TextArea
                  id="txn-description"
                  value={txnDescription}
                  onChange={(e) => setTxnDescription(e.target.value)}
                  placeholder="Optional details or reference"
                />
              </FormField>

              {txnError && <ErrorText>{txnError}</ErrorText>}

              <ModalActions>
                <SecondaryButton type="button" onClick={closeTxnModal} disabled={txnSaving}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={txnSaving}>
                  {txnSaving ? 'Saving...' : 'Save transaction'}
                </PrimaryButton>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalBackdrop>
      )}

      {supportsTransactions && detailParty && (
        <ModalBackdrop>
          <ModalCard style={{ maxWidth: 860 }}>
            <ModalTitle>{detailParty.name}</ModalTitle>
            <ModalSubtitle>
              View all recorded transactions for this {labelLower} and see how the current balance was reached.
            </ModalSubtitle>

            {(() => {
              const beginning = Number(detailParty.amount || 0) || 0;
              const net = partyPaidTotals[detailParty.id] || 0;
              const balance = beginning + net;
              const balanceLabel = isLenderLedger ? 'Current balance to pay' : 'Current balance to receive';
              return (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '12px',
                    marginBottom: 8
                  }}
                >
                  <div
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: '10px 12px'
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 12,
                        color: '#94a3b8',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Beginning amount
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                      {currencyFormatter.format(beginning)}
                    </p>
                  </div>
                  <div
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: '10px 12px'
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 12,
                        color: '#94a3b8',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Net movements
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                      {currencyFormatter.format(net)}
                    </p>
                  </div>
                  <div
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: '10px 12px'
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 12,
                        color: '#94a3b8',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}
                    >
                      {balanceLabel}
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                      {currencyFormatter.format(balance)}
                    </p>
                  </div>
                </div>
              );
            })()}

            <div style={{ maxHeight: 360, overflow: 'auto', marginTop: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '6px 4px' }}>Date</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
                    <th style={{ padding: '6px 4px' }}>Flow</th>
                    <th style={{ padding: '6px 4px' }}>Type</th>
                    <th style={{ padding: '6px 4px' }}>Bank</th>
                    <th style={{ padding: '6px 4px' }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {partyTransactionsLoading ? (
                    <tr>
                      <td colSpan={6} style={{ padding: '12px 4px', textAlign: 'center', color: '#94a3b8' }}>
                        Loading transactions...
                      </td>
                    </tr>
                  ) : partyTransactionsError ? (
                    <tr>
                      <td colSpan={6} style={{ padding: '12px 4px', textAlign: 'center', color: '#dc2626' }}>
                        {partyTransactionsError}
                      </td>
                    </tr>
                  ) : partyTransactions.length > 0 ? (
                    partyTransactions
                      .slice()
                      .sort((a, b) => {
                        const timeA = a.date ? new Date(a.date).getTime() : 0;
                        const timeB = b.date ? new Date(b.date).getTime() : 0;
                        if (timeA === timeB) {
                          return (b.id || 0) - (a.id || 0);
                        }
                        return timeB - timeA;
                      })
                      .map((txn) => {
                        const amt = Number(txn.amount) || 0;
                        const isReceive = (txn.direction || 'pay') === 'receive';
                        const prefix = isReceive ? '+' : '-';
                        const color = isReceive ? '#15803d' : '#b91c1c';
                        const holder = txn.accountHolderName || txn.account_holder_name || '';
                        const bankLabel = [txn.bankName, holder].filter(Boolean).join(' — ');

                        return (
                          <tr key={txn.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>
                              {txn.date ? String(txn.date).slice(0, 10) : ''}
                            </td>
                            <td
                              style={{
                                padding: '6px 4px',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                                color
                              }}
                            >
                              {`${prefix}${currencyFormatter.format(Math.abs(amt))}`}
                            </td>
                            <td style={{ padding: '6px 4px', textTransform: 'capitalize' }}>{txn.direction || ''}</td>
                            <td style={{ padding: '6px 4px', textTransform: 'capitalize' }}>{txn.sourceType || ''}</td>
                            <td style={{ padding: '6px 4px' }}>{bankLabel || (txn.sourceType === 'cash' ? 'Cash' : '—')}</td>
                            <td style={{ padding: '6px 4px' }}>{txn.description || '—'}</td>
                          </tr>
                        );
                      })
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ padding: '12px 4px', textAlign: 'center', color: '#94a3b8' }}>
                        No transactions recorded for this {labelLower} yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <ModalActions>
              <SecondaryButton type="button" onClick={closePartyDetail}>
                Close
              </SecondaryButton>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}
    </PageSection>
  );
};

export default PartyLedgerPage;
